import Phaser from 'phaser';

import { preloadStaticLetterPartyAssets } from '@/features/letterParty/game/rendering/assets';
import Board from '@/features/letterParty/game/rendering/board';
import LottieCanvas from '@/features/letterParty/game/rendering/lottieCanvas';

import type { Direction, LetterPartyTheme } from '@/features/letterParty/types';
import type { LetterPartyBoardState } from '@/features/letterParty/game/rendering/board';

export type { LetterPartyBoardState } from '@/features/letterParty/game/rendering/board';

export const LETTER_PARTY_SCENE_KEY = 'LetterPartyScene';

const LETTER_TEXT_COLOR = '#260466';
const SWIPE_THRESHOLD = 30;

type SwipeDirectionHandler = (direction: Direction) => void;

export default class LetterPartyScene extends Phaser.Scene {
	private boardRenderer: Board;
	private boardState: LetterPartyBoardState | null = null;
	private isReady = false;
	private lottieRenderer: LottieCanvas;
	private onSwipeDirection: SwipeDirectionHandler;

	constructor(onSwipeDirection: SwipeDirectionHandler, private readonly theme: LetterPartyTheme) {
		super(LETTER_PARTY_SCENE_KEY);
		this.onSwipeDirection = onSwipeDirection;
		this.lottieRenderer = new LottieCanvas(this, LETTER_TEXT_COLOR);
		this.boardRenderer = new Board(this, this.lottieRenderer, theme);
	}

	preload() {
		preloadStaticLetterPartyAssets(this, this.theme);
	}

	create() {
		this.isReady = true;
		this.scale.on(Phaser.Scale.Events.RESIZE, this.renderBoard);
		this.input.on(Phaser.Input.Events.POINTER_UP, this.handlePointerUp);
		this.renderBoard();

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanUp);
		this.events.once(Phaser.Scenes.Events.DESTROY, this.cleanUp);
	}

	update() {
		this.lottieRenderer.update();
	}

	public updateBoard(state: LetterPartyBoardState) {
		this.boardState = state;
		this.renderBoard();
	}

	private cleanUp = () => {
		this.isReady = false;
		this.scale.off(Phaser.Scale.Events.RESIZE, this.renderBoard);
		this.input.off(Phaser.Input.Events.POINTER_UP, this.handlePointerUp);
		this.boardRenderer.clear();
		this.lottieRenderer.clear();
	};

	private handlePointerUp = (pointer: Phaser.Input.Pointer) => {
		const deltaX = pointer.upX - pointer.downX;
		const deltaY = pointer.upY - pointer.downY;
		const absDeltaX = Math.abs(deltaX);
		const absDeltaY = Math.abs(deltaY);

		const threshold = SWIPE_THRESHOLD * (this.boardState?.pixelRatio ?? 1);

		if (Math.max(absDeltaX, absDeltaY) < threshold) {
			return;
		}

		if (absDeltaX > absDeltaY) {
			this.onSwipeDirection(deltaX < 0 ? 'LEFT' : 'RIGHT');
			return;
		}

		this.onSwipeDirection(deltaY < 0 ? 'UP' : 'DOWN');
	};

	private renderBoard = () => {
		if (!this.isReady || !this.boardState) {
			return;
		}

		this.boardRenderer.renderBoard(this.boardState);
	};
}
